import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Button from '../../components/Button';
import { ArrowLeft, Save, Euro } from 'lucide-react';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
}

interface Quote {
  _id: string;
  reference: string;
  clientId: Client;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  clientId: Client;
}

interface Payment {
  _id: string;
  paymentNumber: string;
  amount: number;
  currency: string;
  paymentDate: string;
  paymentMethod: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  invoiceId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const EditPayment: React.FC = () => {
  const params = useParams<{ id: string }>();
  const paymentId = (params as any)?.id || (params as any)?.paymentId;
  const navigate = useNavigate();
  const [payment, setPayment] = useState<Payment | null>(null);
  const [formData, setFormData] = useState<Partial<Payment>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!paymentId) {
        setError('Payment ID is missing.');
        setIsLoading(false);
        return;
      }
      try {
        const paymentResponse = await apiService.payments.getById(paymentId);
        const paymentData = paymentResponse?.data?.data?.payment ?? paymentResponse?.data?.payment ?? paymentResponse?.data?.data ?? paymentResponse?.data;
        if (!paymentData) {
          throw new Error('Payment data not found in response.');
        }
        setPayment(paymentData);
        setFormData({
          paymentNumber: paymentData.paymentNumber,
          amount: paymentData.amount,
          currency: paymentData.currency,
          paymentDate: paymentData.paymentDate ? new Date(paymentData.paymentDate).toISOString().split('T')[0] : '',
          paymentMethod: paymentData.paymentMethod,
          status: paymentData.status,
          invoiceId: paymentData.invoiceId,
          notes: paymentData.notes,
        });

        const clientsResponse = await apiService.users.getAll({ params: { role: 'client' } });
        const clientsData = clientsResponse?.items || [];
        setClients(clientsData);

        const invoicesResponse = await apiService.invoices.getAll();
        const invoicesData = invoicesResponse?.items || [];
        if (!invoicesData) {
          throw new Error('Invoices data not found in response.');
        }
        setInvoices(invoicesData);

      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load payment details or related data.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [paymentId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'invoiceId') {
      setFormData({ ...formData, invoiceId: value });
    } else if (name === 'amount') {
      setFormData({ ...formData, [name]: parseFloat(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const toNumber = (v: any, def = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };
  const cleanPayload = (src: Partial<Payment>) => {
    const out: any = { ...src };
    if ('amount' in out) out.amount = toNumber(out.amount, 0);
    Object.keys(out).forEach(k => {
      if (out[k] === '') delete out[k];
    });
    return out;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentId) return;

    setIsSaving(true);
    setError(null);
    try {
      const payload = cleanPayload(formData);
      await apiService.payments.update(paymentId, payload);
      toast.success('Paiement mis à jour avec succès !');
      navigate(`/admin/payments/view/${paymentId}`);
    } catch (err) {
      console.error('Failed to update payment:', err);
      setError('Failed to update payment. Please try again.');
      toast.error('Erreur lors de la mise à jour du paiement.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-gray-600">Chargement des détails du paiement...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-error-600">Erreur: {error}</p>
        </div>
      </Layout>
    );
  }

  if (!payment || !formData) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-gray-600">Paiement non trouvé ou données manquantes.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 pb-24">
        <div className="flex justify-between items-center">
          <div>
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 flex items-center text-primary-600">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour aux Paiements
            </Button>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Euro className="w-8 h-8 mr-3 text-primary-600" />
              Modifier le Paiement: {payment.paymentNumber}
            </h1>
            <p className="text-gray-600 mt-2">
              Mettez à jour les informations du paiement #{payment.paymentNumber}
            </p>
          </div>
          
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="paymentNumber" className="block text-sm font-medium text-gray-700">N° Paiement</label>
              <input
                type="text"
                name="paymentNumber"
                id="paymentNumber"
                value={formData.paymentNumber || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Montant</label>
              <input
                type="number"
                name="amount"
                id="amount"
                value={formData.amount || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700">Devise</label>
              <input
                type="text"
                name="currency"
                id="currency"
                value={formData.currency || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700">Date de Paiement</label>
              <input
                type="date"
                name="paymentDate"
                id="paymentDate"
                value={formData.paymentDate || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700">Méthode</label>
              <input
                type="text"
                name="paymentMethod"
                id="paymentMethod"
                value={formData.paymentMethod || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">Statut</label>
              <select
                name="status"
                id="status"
                value={formData.status || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Sélectionner un statut</option>
                <option value="pending">En attente</option>
                <option value="completed">Terminé</option>
                <option value="failed">Échoué</option>
                <option value="refunded">Remboursé</option>
              </select>
            </div>
            <div>
              <label htmlFor="invoiceId" className="block text-sm font-medium text-gray-700">Facture Liée</label>
              <select
                name="invoiceId"
                id="invoiceId"
                value={formData.invoiceId || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Sélectionner une facture</option>
                {invoices.map((invoice) => (
                  <option key={invoice._id} value={invoice._id}>
                    {invoice.invoiceNumber} - {invoice.clientId.firstName} {invoice.clientId.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes Additionnelles</label>
              <textarea
                name="notes"
                id="notes"
                rows={3}
                value={formData.notes || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              ></textarea>
            </div>
          </div>

          {error && <p className="text-error-600 text-sm mt-4">{error}</p>}

          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10">
            <div className="max-w-4xl mx-auto flex justify-end space-x-3">
              <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
                Annuler
              </Button>
              <Button type="submit" disabled={isSaving}>
                <Save className="w-5 h-5 mr-2" />
                {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </div>
          </div>
        </form>      
      </div>
    </Layout>
  );
};

export default EditPayment;
